/* eslint-disable security/detect-object-injection */

import './PlanTaskLayout.style.less'
import {
  ActionField,
  CommentField,
  DocumentField,
  PlanAssigneeField,
  PlanDateField,
  PlanHistoryField,
  PlanLabelField,
  ProgressField,
  StatusField,
} from '../Fields'
import { Col, notification, Row, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { cloneDeep, set } from 'lodash'
import moment from 'moment'

import { PlanStatuses } from '../Fields/StatusField/StatusField.enum'
import {
  updateTaskPlanDate,
  updateTaskStatus,
} from '@/screens/Order/Pages/Order.service'
import {
  getOrderAndDocumentCountByTask,
  updateTaskAssignee,
} from '@/screens/Order/Pages/Order.transform'
import { queryClient } from '@/config/react-query'
import { useQuery } from 'react-query'
import { isEmptyValue } from '@/utils/common'
import PBFPermission from '@/screens/Order/components/PBFPermission'
import { useAtom } from 'jotai'
import { currentSelectedCollaborator } from '@/jotai/order.jotai'
import { useAuth } from '@/providers/auth.provider'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT } from '@/constant/date'

function PlanTaskLayout(props: {
  data: any
  planLabel: string
  hasPercentage?: boolean
}) {
  const templateData: any = cloneDeep(props.data)

  const { planLabel } = props

  const {
    json_value,
    id,
    activities,
    plan_start_date,
    plan_end_date,
    value,
    assignments,
    is_range_date,
  } = templateData

  const data_json: any = json_value

  const [currentProgressInput, setcurrentProgressInput] = useState('0')

  const [currentCollaborator] = useAtom(currentSelectedCollaborator)
  const { user } = useAuth()

  const findIsAssignedAndIsUpdater = () => {
    const isUserIsCollaborator = currentCollaborator?.find(
      (collaborator) => collaborator?.user?.id === user?.id
    )
    const currentUserAssigned = props.data.assignments?.find(
      (assignment) => assignment?.user?.id === isUserIsCollaborator?.user.id
    )
    return (
      currentUserAssigned?.user?.id == user?.id &&
      isUserIsCollaborator.roles?.role === 'updater' &&
      !['submitted'].includes(props.data.value)
    )
  }

  const [loading, setloading] = useState(false)

  const { data: commentDialogCount, isLoading: commentDialogLoading } =
    useQuery(['GET_COMMENT_COUNT', id], async () => {
      return await getOrderAndDocumentCountByTask(id)
    })

  const prefetchTodos = async () => {
    // The results of this query will be cached like a normal query
    await queryClient.prefetchQuery('ORDER_OVERVIEW')
  }

  const onInitializeData = () => {
    setcurrentProgressInput(data_json?.current_progress ?? null)
  }

  useEffect(() => onInitializeData(), [])

  // useMutation
  const { name, type }: any = cloneDeep(props.data)

  const onSetPlan = async (
    newPlanDate: moment.Moment,
    newEndDate: moment.Moment
  ) => {
    const planStartDate = dateFormat(
      newPlanDate.toISOString(),
      DATE_FORMAT.YYYYMMDD
    )

    const planEndDate = dateFormat(
      newEndDate.toISOString(),
      DATE_FORMAT.YYYYMMDD
    )
    const isRangeDate = !newPlanDate.isSame(newEndDate)
    const payload = {
      plan_start_date: planStartDate,
      plan_end_date: planEndDate,
      is_range_date: isRangeDate,
    }

    setloading(true)
    await updateTaskPlanDate(id, payload).catch((error) => {
      notification.error({
        message:
          error.response?.data?.message || 'Failed to update task status',
        description: error.message,
        placement: 'top',
        duration: 3,
      })
    })
    await prefetchTodos()
    setloading(false)
  }

  const onUpdatePlan = async (newStatus: string, actualDate: any) => {
    let task = {}
    let status: any = newStatus
    if (isEmptyValue(status)) {
      if (value === newStatus) {
        set(task, 'value', 'planned')
        if (props.data.type !== 'percentage_progress_type')
          set(task, 'activity_value', 'planned')
        else set(task, 'activity_value', 'planned')
      } else if (
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === 'not applicable'
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    } else {
      if (status === 'planned') {
        set(task, 'progress_status', 'in_progress')
      } else if (
        parseInt(status) === 100 ||
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === 'not applicable'
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    }

    if (status === 'planned') set(task, 'value', 'planned')
    else set(task, 'value', `${status}`)
    set(
      task,
      'json_value.plan_date',
      plan_start_date ? dateFormat(plan_start_date, DATE_FORMAT.YYYYMMDD) : null
    )
    set(task, 'json_value.current_status', status)
    set(task, 'activity_value', status)
    set(
      task,
      'actual_date',
      actualDate ? dateFormat(actualDate, DATE_FORMAT.YYYYMMDD) : null
    )

    setloading(true)
    await updateTaskStatus(id, task).catch((error) => {
      notification.error({
        message:
          error.response?.data?.message || 'Failed to update task status',
        description: error.message,
        placement: 'top',
        duration: 3,
      })
    })
    await prefetchTodos()
    setloading(false)
  }

  const onSetAssignee = async (assignee) => {
    setloading(true)
    await updateTaskAssignee(id, assignee).catch((error) => {
      notification.error({
        message: error.response?.data?.message || 'Failed to update assignee',
        description: error.message,
        placement: 'top',
        duration: 3,
      })
    })
    await prefetchTodos()
    setloading(false)
  }

  return (
    <Spin spinning={loading}>
      <div className="task-plan-comp plan-task-layout-container">
        <PBFPermission
          roles={[
            'owner',
            'doer',
            'editor',
            findIsAssignedAndIsUpdater() && 'updater',
          ]}
          showComponent={true}
        >
          <Row align={'middle'}>
            <Col span={6}>
              <PlanLabelField label={name} />
            </Col>
            <Col span={4}>
              <div className="pl-pr-15">
                <div className="mb-2">{planLabel}</div>
                <PBFPermission
                  roles={['owner', 'doer', 'editor']}
                  showComponent
                >
                  <PlanDateField
                    fieldName={name}
                    planStartDate={plan_start_date}
                    planEndDate={plan_end_date}
                    isRangeDate={is_range_date}
                    onSetPlan={onSetPlan}
                  />
                </PBFPermission>
              </div>
            </Col>
            <Col span={4}>
              <div className="pl-pr-15">
                <div className="mb-2">Assignee</div>
                <PlanAssigneeField
                  assignee={assignments?.[0]}
                  fieldName={name}
                  onSetAssignee={onSetAssignee}
                  taskId={id}
                />
              </div>
            </Col>
            <Col span={4}>
              <div className="pl-pr-15">
                <div className="mb-2">Status</div>
                {props.data.progress_status === 'new' ? (
                  <StatusField
                    onApprovalChange={onUpdatePlan}
                    data={props.data}
                    current_status={`Not Planned`}
                    task_type={type}
                    fieldName={name}
                  />
                ) : props.data.type !== 'percentage_progress_type' ? (
                  <StatusField
                    onApprovalChange={onUpdatePlan}
                    data={props.data}
                    current_status={value}
                    task_type={type}
                    fieldName={name}
                  />
                ) : (
                  <ProgressField
                    current_progress={parseInt(value) ?? 0}
                    onCurrentProgressChange={(progress) => {
                      setcurrentProgressInput(progress)
                    }}
                    onSet={(value) => {
                      onUpdatePlan(value, null)
                    }}
                  />
                )}
              </div>
            </Col>
            <Col span={6}>
              <PBFPermission
                roles={[
                  'owner',
                  'doer',
                  'editor',
                  findIsAssignedAndIsUpdater() && 'updater',
                ]}
              >
                <div className="pl-pr-15">
                  {props.data?.progress_status !== 'new' && (
                    <>
                      <ActionField
                        data={templateData}
                        current_status={value}
                        type={type}
                        onStatusChanged={(
                          status: PlanStatuses,
                          actualDate: any
                        ) => {
                          onUpdatePlan(status, actualDate)
                        }}
                        onPercentageChanged={() => {
                          if (!currentProgressInput)
                            return notification.error({
                              message: `Value cannot be 0`,
                              placement: 'bottom',
                              duration: 3,
                            })
                          if (isNaN(currentProgressInput as any)) return
                          if (
                            Number(currentProgressInput) <=
                            Number(templateData.value)
                          )
                            return
                          onUpdatePlan(currentProgressInput, new Date())
                        }}
                      />
                    </>
                  )}
                </div>
              </PBFPermission>
            </Col>
          </Row>
        </PBFPermission>

        <Row>
          <Col span={6} />
          <Col span={4}>
            <Row align={'middle'}>
              <CommentField label={name} taskId={id} data={templateData} />
              {!commentDialogLoading &&
                commentDialogCount?.comment_counts !== 0 && (
                  <p className="count-document">
                    {commentDialogCount?.comment_counts}
                  </p>
                )}
            </Row>
          </Col>
          <Col span={4}>
            <Row align={'middle'}>
              <DocumentField label={name} taskId={templateData.id} />
              {!commentDialogLoading &&
                commentDialogCount?.document_counts !== 0 && (
                  <p className="count-document">
                    {commentDialogCount?.document_counts}
                  </p>
                )}
            </Row>
          </Col>
          <Col span={4}>
            <div className="pl-pr-15">
              {props.data?.activities?.length > 0 && (
                <PlanHistoryField activity={activities} />
              )}
            </div>
          </Col>
          <Col span={6} />
        </Row>
      </div>
    </Spin>
  )
}

export default PlanTaskLayout
